import { gql, useMutation } from '@apollo/client';
import IconBook from '@apollo/icons/default/IconBook.svg';
import {
  AlertBanner,
  Button,
  ButtonGroup,
  Code,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Text,
  Tooltip,
  useToast,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback } from 'react';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { DocsRouter } from 'src/lib/routers/docs';
import { Yup } from 'src/lib/yup';

import { GraphRef } from '../../hooks/useGraphRef';

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  url: Yup.string()
    .required('Please provide a webhook URL.')
    .url('Please provide a valid webhook URL.'),
  secretToken: Yup.string().nullable(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

const CustomCheckConfigurationResultFragment = gql`
  fragment CustomCheckConfigurationResultFragment on CustomCheckConfigurationResult {
    ... on PermissionError {
      message
    }
    ... on ValidationError {
      message
    }
    ... on CustomCheckConfiguration {
      id
      channel {
        id
        url
        secretTokenIsSet
        lastUpdatedBy {
          id
        }
        lastUpdatedAt
      }
    }
  }
`;
const VariantSetCustomCheckConfiguration = gql`
  mutation VariantSetCustomCheckConfiguration(
    $input: SetCustomCheckConfigurationInput!
    $graphVariant: String!
    $graphId: ID!
  ) {
    graph(id: $graphId) {
      variant(name: $graphVariant) {
        setCustomCheckConfiguration(input: $input) {
          ...CustomCheckConfigurationResultFragment
        }
      }
    }
  }
  ${CustomCheckConfigurationResultFragment}
`;

const GraphSetCustomCheckConfiguration = gql`
  mutation GraphSetCustomCheckConfiguration(
    $input: SetCustomCheckConfigurationInput!
    $graphId: ID!
  ) {
    graph(id: $graphId) {
      setCustomCheckConfiguration(input: $input) {
        ...CustomCheckConfigurationResultFragment
      }
    }
  }
  ${CustomCheckConfigurationResultFragment}
`;

export const CustomChecksRegisteredWebhookModal = ({
  type,
  graphRef,
  isOpen,
  onClose,
  currentEndpointUrl,
  secretTokenIsSet,
}: {
  type: 'graph' | 'variant';
  graphRef: GraphRef;
  isOpen: boolean;
  onClose: () => void;
  currentEndpointUrl: string;
  secretTokenIsSet: boolean;
}) => {
  const [
    variantSetCustomCheckConfiguration,
    { loading: variantMutationLoading },
  ] = useMutation<
    GraphQLTypes.VariantSetCustomCheckConfiguration,
    GraphQLTypes.VariantSetCustomCheckConfigurationVariables
  >(VariantSetCustomCheckConfiguration);
  const [graphSetCustomCheckConfiguration, { loading: graphMutationLoading }] =
    useMutation<
      GraphQLTypes.GraphSetCustomCheckConfiguration,
      GraphQLTypes.GraphSetCustomCheckConfigurationVariables
    >(GraphSetCustomCheckConfiguration);
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const toast = useToast();

  const {
    values,
    touched,
    errors,
    dirty,
    handleChange,
    isSubmitting,
    submitForm,
    setFieldValue,
    resetForm,
  } = useFormik<FormValues>({
    initialValues: {
      url: currentEndpointUrl,
      secretToken: null,
    },
    enableReinitialize: true,
    async onSubmit(formValues) {
      const mutationFunction =
        type === 'variant'
          ? variantSetCustomCheckConfiguration
          : graphSetCustomCheckConfiguration;

      const { data, errors: responseErrors } = await mutationFunction({
        variables: {
          graphId: graphRef.graphId,
          input: { url: formValues.url, secretToken: formValues.secretToken },
          graphVariant: graphRef.graphVariant,
        },
      });

      const response = data?.graph
        ? 'setCustomCheckConfiguration' in data.graph
          ? data.graph.setCustomCheckConfiguration
          : 'variant' in data.graph
            ? data.graph.variant?.setCustomCheckConfiguration
            : null
        : null;

      if (response?.__typename === 'CustomCheckConfiguration') {
        onClose();
        setErrorMessage(undefined);
        setFieldValue('secretToken', null); // reset secret token so when user reopens model we cleared it and are not saving it in browser state
        toast({
          duration: 3000,
          isClosable: false,
          status: 'success',
          description:
            "You've successfully updated the configuration of for your custom check.",
        });
        return;
      }

      if (
        response?.__typename === 'PermissionError' ||
        response?.__typename === 'ValidationError' ||
        !response
      ) {
        setErrorMessage(
          response?.message ??
            'An error occurred while saving the webhook URL. Please try again',
        );
      } else if (isMinLengthArray(1, responseErrors)) {
        setErrorMessage(`An error occurred: ${responseErrors[0].message}.`);
      } else {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            response,
            'An error occurred while saving the webhook URL. Please try again',
          ),
        );
      }
    },
    validationSchema,
  });

  const handleClose = useCallback(() => {
    setErrorMessage(undefined);
    resetForm({});
    onClose();
  }, [onClose, resetForm]);

  return (
    <Modal isOpen={isOpen} size="2xl" onClose={handleClose}>
      <ModalOverlay />
      <ModalContent className="overflow-y-visible">
        <ModalHeader>
          <ModalTitle>Edit custom check webhook</ModalTitle>
          <ModalDescription>
            Custom webhooks require you to set up an <Code>HTTPS</Code> endpoint
            accessible via the public internet.
          </ModalDescription>
        </ModalHeader>
        <ModalBody className="overflow-y-visible">
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <FormControl isInvalid={!!(touched.url && errors.url)}>
            <FormLabel>Endpoint URL</FormLabel>
            <Input
              autoFocus={true}
              type="text"
              name="url"
              onChange={handleChange}
              placeholder="e.g https://webhook.com/endpoint"
              value={values.url}
            />
            {errors.url && <FormErrorMessage>{errors.url}</FormErrorMessage>}
          </FormControl>
          <FormControl
            isInvalid={!!(touched.secretToken && errors.secretToken)}
          >
            <FormLabel>Secret token (optional)</FormLabel>
            <Text>
              If provided, notification{' '}
              <span>
                <Code>HTTP</Code>
              </span>{' '}
              requests will include an{' '}
              <Code className="items-center">x-apollo-signature</Code> header as{' '}
              <span className="italic">Hash Message Authentication Code</span>{' '}
              (HMAC) generated using the token, request body, and SHA256.
              Formatted as <Code>sha256=hmac-value</Code>.
            </Text>
            <Input
              type="text"
              name="secretToken"
              onChange={handleChange}
              placeholder="mySecretToken123"
              value={values.secretToken ?? ''}
            />
            {!values.secretToken && secretTokenIsSet && (
              <AlertBanner>
                The existing secret will be removed, make sure to enter it again
                if you wish to keep it with the new URL
              </AlertBanner>
            )}
            {errors.secretToken && (
              <FormErrorMessage>{errors.secretToken}</FormErrorMessage>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Tooltip label="View documentation" placement="top">
            <IconButton
              aria-label="View documentation"
              variant="secondary"
              as="a"
              target="_blank"
              rel="noopener noreferrer"
              href={DocsRouter.path('CustomChecks')}
            >
              <IconBook />
            </IconButton>
          </Tooltip>
          <ButtonGroup>
            <Button
              variant="secondary"
              type="button"
              onClick={handleClose}
              isDisabled={
                isSubmitting || variantMutationLoading || graphMutationLoading
              }
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              isDisabled={
                !!(touched.secretToken && errors.secretToken) ||
                !!(touched.url && errors.url) ||
                !dirty
              }
              isLoading={
                isSubmitting || variantMutationLoading || graphMutationLoading
              }
              onClick={submitForm}
            >
              Save
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
